/**
 * Shared trusted-domain data and lookups for the scoring module.
 *
 * These Sets are pre-computed once at module load for fast in-memory lookups and
 * are imported by both `heuristics.ts` and `fingerprints.ts` so the trusted-domain
 * data has a single source of truth.
 *
 * Note: commonEmailProviders are intentionally NOT part of STATIC_TRUSTED_DOMAINS -
 * email provider domains (gmail.com, outlook.com, etc.) are just infrastructure and
 * don't indicate trustworthiness of the sender.
 */

import trustedDomainsData from '../data/trusted-domains.json';

// Pre-compute trusted domains set once at module load for fast in-memory lookup
export const STATIC_TRUSTED_DOMAINS = new Set([
  ...(trustedDomainsData.workTools || []),
  ...trustedDomainsData.majorCorporations,
  ...trustedDomainsData.professionalServices,
  ...trustedDomainsData.financialServices,
]);

export const TRUSTED_TLDS = trustedDomainsData.trustedTLDs || [];

// Common email provider domains (excluded from previous domain contact check)
export const COMMON_EMAIL_PROVIDERS = new Set(
  (trustedDomainsData.commonEmailProviders || []).map((d: string) => d.toLowerCase())
);

// Spam-prone domains that get +1 penalty
export const SPAM_PRONE_DOMAINS = new Set(
  (trustedDomainsData.spamProneDomains || []).map((d: string) => d.toLowerCase())
);

/**
 * Check if a domain is trusted (synchronous check, no LLM).
 * Checks static trusted domains list, user's trusted domains, and trusted TLDs.
 *
 * @param domain - Domain to check.
 * @param userTrustedDomains - User's list of trusted domains.
 * @returns True if domain is trusted, False otherwise.
 */
export function isDomainTrustedSync(
  domain: string | null,
  userTrustedDomains: string[] = []
): boolean {
  if (!domain) {
    return false;
  }

  const domainLower = domain.toLowerCase();

  // Check user's trusted domains first (user-specific, highest priority)
  for (const trusted of userTrustedDomains) {
    const trustedLower = trusted.toLowerCase().trim();
    if (!trustedLower) continue;

    // Exact match
    if (domainLower === trustedLower) {
      return true;
    }

    // Subdomain match (e.g., user adds "example.com", matches "sub.example.com")
    if (domainLower.endsWith(`.${trustedLower}`)) {
      return true;
    }
  }

  // Check against static trusted domains list (from JSON file, pre-computed Set)
  if (STATIC_TRUSTED_DOMAINS.has(domainLower)) {
    return true;
  }

  // Check for subdomain matches (e.g., "e.atlassian.com" matches "atlassian.com")
  for (const trustedDomain of STATIC_TRUSTED_DOMAINS) {
    if (domainLower.endsWith(`.${trustedDomain}`)) {
      return true;
    }
  }

  // Check if domain ends with trusted TLDs (e.g., .gov, .mil)
  for (const tld of TRUSTED_TLDS) {
    if (domainLower.endsWith(`.${tld}`) || domainLower === tld) {
      return true;
    }
  }

  return false;
}
